import { ADDRESS_ZERO } from '@uniswap/router-sdk';
import { Decimal } from 'decimal.js';
import { BigNumber, ethers } from 'ethers';
import { UniswapSDK } from '@/providers/swap/sdk';
import { swapSimple } from '@/providers/swap';
import { CryptoAssetType, type CryptoAsset } from '@/types/WalletTypes';
import type {
  ExecuteSwapParams,
  GetMaxSwapAmountParams,
  GetSwapQuoteParams,
  ISwapProvider,
  MaxSwapAmountResult,
  SwapExecutionResult,
  SwapQuoteResult,
} from './ISwapProvider';

type UniswapSwapProviderOptions = {
  chainId: number;
  jsonRpcUrl: string;
};

export class UniswapSwapProvider implements ISwapProvider {
  private chainId: number;
  private initialized = false;
  private provider: ethers.providers.JsonRpcProvider;
  private uniswapSdk: UniswapSDK;

  constructor({ chainId, jsonRpcUrl }: UniswapSwapProviderOptions) {
    this.chainId = chainId;
    this.provider = new ethers.providers.JsonRpcProvider(jsonRpcUrl, chainId);
    this.uniswapSdk = new UniswapSDK({ jsonRpcUrl, chainId });
  }

  async getSwapQuote({
    fromCryptoAsset,
    toCryptoAsset,
    amount,
  }: GetSwapQuoteParams): Promise<SwapQuoteResult | null> {
    if (!this.isAllowedSellToken(fromCryptoAsset)) {
      throw new Error(
        `Token ${fromCryptoAsset.asset.canonicalName} is not enabled for swaps`
      );
    }

    if (!this.isAllowedBuyToken(toCryptoAsset)) {
      throw new Error(
        `Token ${toCryptoAsset.asset.canonicalName} is not allowed as a swap target`
      );
    }

    await this.ensureInitialized();

    const quote = await this.uniswapSdk.getQuote({
      fromTokenAddress: this.toUniswapTokenAddress(fromCryptoAsset),
      toTokenAddress: this.toUniswapTokenAddress(toCryptoAsset),
      amountIn: amount.toString(),
    });

    if (!quote) {
      return null;
    }

    return {
      estimatedAmountOutDecimal: quote.estimatedAmountOut,
      estimatedGasFeeWei: await this.getEstimatedGasFeeWei(quote.estimatedGasAmount),
    };
  }

  async getMaxSwapAmount({
    balance,
    precision,
    priceUsd,
    ...quoteParams
  }: GetMaxSwapAmountParams): Promise<MaxSwapAmountResult> {
    const balanceAmount = new Decimal(balance || 0);
    let maxCoin = balanceAmount;

    if (this.getSwapAssetType(quoteParams.fromCryptoAsset) === CryptoAssetType.Native) {
      const quote = await this.getSwapQuote({ ...quoteParams, amount: balance });
      const gasFee = quote?.estimatedGasFeeWei
        ? new Decimal(
            ethers.utils.formatUnits(
              quote.estimatedGasFeeWei,
              quoteParams.fromCryptoAsset.fee.decimalPrecision
            )
          )
        : new Decimal(0);
      maxCoin = Decimal.max(0, balanceAmount.minus(gasFee));
    }

    const formattedMaxCoin = maxCoin.toFixed(precision);
    const maxFiat =
      Number(priceUsd) > 0 ? maxCoin.mul(new Decimal(priceUsd)).toFixed(2) : null;

    return {
      maxCoin: formattedMaxCoin,
      maxFiat,
    };
  }

  async executeSwap({
    fromCryptoAsset,
    toCryptoAsset,
    amount,
    slippage = 0.3,
    wallet,
  }: ExecuteSwapParams): Promise<SwapExecutionResult> {
    if (!wallet?.privateKey) {
      throw new Error('Private-key-backed wallet is required for Uniswap swaps');
    }

    if (!this.isAllowedSellToken(fromCryptoAsset)) {
      throw new Error(
        `Token ${fromCryptoAsset.asset.canonicalName} is not enabled for swaps`
      );
    }

    if (!this.isAllowedBuyToken(toCryptoAsset)) {
      throw new Error(
        `Token ${toCryptoAsset.asset.canonicalName} is not allowed as a swap target`
      );
    }

    await this.ensureInitialized();
    const fromTokenAddress = this.toUniswapTokenAddress(fromCryptoAsset);
    const toTokenAddress = this.toUniswapTokenAddress(toCryptoAsset);

    const amountOutMinimum = await this.getAmountOutMinimum({
      fromTokenAddress,
      toTokenAddress,
      amountIn: amount.toString(),
      slippagePercentage: slippage,
    });

    const result = await swapSimple({
      privateKey: wallet.privateKey,
      currencyIn: fromTokenAddress,
      currencyOut: toTokenAddress,
      amountIn: amount.toString(),
      amountOutMinimum,
    });

    if (!result || !('hash' in result) || !result.hash) {
      throw new Error('Uniswap swap failed');
    }

    return {
      transactionHash: result.hash,
    };
  }

  isAllowedBuyToken(cryptoAsset: CryptoAsset): boolean {
    return this.isConfiguredForSwapChain(cryptoAsset);
  }

  isAllowedSellToken(cryptoAsset: CryptoAsset): boolean {
    if (!this.isConfiguredForSwapChain(cryptoAsset)) {
      return false;
    }

    if (this.getSwapAssetType(cryptoAsset) === CryptoAssetType.Native) {
      return true;
    }

    return cryptoAsset.asset.settings?.allowSell === true;
  }

  private toUniswapTokenAddress(cryptoAsset: CryptoAsset): string {
    const address =
      this.getSwapAssetType(cryptoAsset) === CryptoAssetType.Native
        ? ADDRESS_ZERO
        : cryptoAsset.asset.definitionAddress;

    if (!address) {
      throw new Error(
        `Token ${cryptoAsset.asset.canonicalName} does not have a contract address for chain ${this.chainId}`
      );
    }

    return address;
  }

  private getSwapAssetType(cryptoAsset: CryptoAsset): CryptoAssetType {
    return cryptoAsset.asset.assetType.id;
  }

  private isConfiguredForSwapChain(
    cryptoAsset?: CryptoAsset | null
  ): cryptoAsset is CryptoAsset {
    if (!cryptoAsset) return false;

    return (
      cryptoAsset.asset.blockchainConfiguration.chainId === this.chainId &&
      (this.getSwapAssetType(cryptoAsset) === CryptoAssetType.Native ||
        this.getSwapAssetType(cryptoAsset) === CryptoAssetType.Erc20)
    );
  }

  private async ensureInitialized(): Promise<void> {
    if (this.initialized) {
      return;
    }

    await this.uniswapSdk.init();
    this.initialized = true;
  }

  private async getEstimatedGasFeeWei(estimatedGasAmount: string): Promise<string> {
    const feeData = await this.provider.getFeeData();
    const gasPrice =
      feeData.maxFeePerGas || feeData.gasPrice || ethers.utils.parseUnits('20', 'gwei');

    return BigNumber.from(estimatedGasAmount).mul(gasPrice).toString();
  }

  private async getAmountOutMinimum({
    fromTokenAddress,
    toTokenAddress,
    amountIn,
    slippagePercentage,
  }: {
    fromTokenAddress: string;
    toTokenAddress: string;
    amountIn: string;
    slippagePercentage: number;
  }): Promise<string> {
    if (slippagePercentage <= 0) {
      return '0';
    }

    const quote = await this.uniswapSdk.getQuote({
      fromTokenAddress,
      toTokenAddress,
      amountIn,
      slippagePercentage,
    });

    return quote?.amountOutMinimum || '0';
  }
}
